import React, { useState, useEffect } from 'react';
import { PageHeader, Btn, Badge, Table, Modal, Input, Select, Empty } from '../../components/UI';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const TAG_COLORS = { hot: '#ff6b6b', warm: '#ffd700', cold: '#4da8ff', customer: '#00E74C', blacklist: '#666' };
const TAGS = [{ value: 'cold', label: '🔵 Cold' }, { value: 'warm', label: '🟡 Warm' }, { value: 'hot', label: '🔴 Hot' }, { value: 'customer', label: '✅ Customer' }, { value: 'blacklist', label: '🚫 Blacklist' }];
const STAGES = [{ value: 'new', label: 'New' }, { value: 'contacted', label: 'Contacted' }, { value: 'interested', label: 'Interested' }, { value: 'negotiation', label: 'Negotiation' }, { value: 'closed', label: 'Closed' }];

const empty = { name: '', whatsapp: '', email: '', address: '', tag: 'cold', pipeline_stage: 'new', deal_value: '', birthday: '', notes: '' };

export default function CRM() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/contacts', { params: { search, tag: tagFilter, stage: stageFilter } });
      if (data.success) setContacts(data.contacts);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadContacts(); }, [search, tagFilter, stageFilter]);

  const openAdd = () => { setForm(empty); setEditId(null); setModal(true); };
  const openEdit = (c) => { setForm({ name: c.name || '', whatsapp: c.whatsapp || '', email: c.email || '', address: c.address || '', tag: c.tag || 'cold', pipeline_stage: c.pipeline_stage || 'new', deal_value: c.deal_value || '', birthday: c.birthday ? c.birthday.split('T')[0] : '', notes: c.notes || '' }); setEditId(c.id); setModal(true); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, deal_value: form.deal_value || null, birthday: form.birthday || null };
      const { data } = editId ? await api.put(`/api/contacts/${editId}`, payload) : await api.post('/api/contacts', payload);
      if (data.success) { toast.success(editId ? 'Contact updated!' : 'Contact added!'); setModal(false); loadContacts(); }
      else toast.error(data.message);
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    const { data } = await api.delete(`/api/contacts/${id}`);
    if (data.success) { toast.success('Deleted'); loadContacts(); }
  };

  // Pipeline counts
  const counts = STAGES.reduce((acc, s) => { acc[s.value] = contacts.filter(c => c.pipeline_stage === s.value).length; return acc; }, {});

  return (
    <div>
      <PageHeader title="CRM Contacts" action={<Btn onClick={openAdd}>➕ Add Contact</Btn>} />

      {/* Pipeline Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 24 }}>
        {STAGES.map(s => (
          <div key={s.value} onClick={() => setStageFilter(stageFilter === s.value ? '' : s.value)} style={{ background: stageFilter === s.value ? 'var(--green-dim)' : 'var(--bg-card)', border: `1px solid ${stageFilter === s.value ? 'var(--green)' : 'var(--border)'}`, borderRadius: 10, padding: 14, textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s' }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 22, color: stageFilter === s.value ? 'var(--green)' : 'var(--text)' }}>{counts[s.value] || 0}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search name, phone, email..." style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 14px', color: 'var(--text)', fontSize: 13, outline: 'none', minWidth: 220 }} />
        <select value={tagFilter} onChange={e => setTagFilter(e.target.value)} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 14px', color: 'var(--text)', fontSize: 13 }}>
          <option value="">All Tags</option>
          {TAGS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 14px', color: 'var(--text)', fontSize: 13 }}>
          <option value="">All Stages</option>
          {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <Table
          loading={loading}
          emptyMsg="No contacts found. Add your first contact!"
          columns={[
            { key: 'name', label: 'Name', render: (v, r) => <div><div style={{ fontWeight: 600 }}>{v}</div>{r.birthday && <div style={{ fontSize: 11, color: '#ffd700' }}>🎂 {new Date(r.birthday).toLocaleDateString('en-IN')}</div>}</div> },
            { key: 'whatsapp', label: 'WhatsApp', render: v => <span style={{ color: 'var(--green)' }}>{v || '—'}</span> },
            { key: 'email', label: 'Email', render: v => <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{v || '—'}</span> },
            { key: 'tag', label: 'Tag', render: v => <span style={{ background: `${TAG_COLORS[v]}22`, color: TAG_COLORS[v], padding: '3px 10px', borderRadius: 100, fontSize: 11, fontWeight: 700 }}>{v?.toUpperCase()}</span> },
            { key: 'pipeline_stage', label: 'Stage', render: v => <Badge>{v}</Badge> },
            { key: 'deal_value', label: 'Deal', render: v => v ? `₹${parseFloat(v).toLocaleString()}` : '—' },
            { key: 'id', label: 'Actions', render: (v, r) => (
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => openEdit(r)} style={{ background: 'rgba(77,168,255,0.1)', color: '#4da8ff', border: 'none', padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>✏️</button>
                <button onClick={() => del(r.id)} style={{ background: 'rgba(255,68,68,0.1)', color: '#ff6b6b', border: 'none', padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>🗑</button>
              </div>
            )},
          ]}
          data={contacts}
        />
      </div>

      {/* Add/Edit Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title={editId ? '✏️ Edit Contact' : '➕ Add Contact'} width={540}>
        <form onSubmit={save}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full name" required />
            <Input label="WhatsApp *" value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} placeholder="+91 9876543210" required />
            <Input label="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" />
            <Input label="Birthday" type="date" value={form.birthday} onChange={e => setForm({ ...form, birthday: e.target.value })} />
            <Select label="Tag" value={form.tag} onChange={e => setForm({ ...form, tag: e.target.value })} options={TAGS} />
            <Select label="Pipeline Stage" value={form.pipeline_stage} onChange={e => setForm({ ...form, pipeline_stage: e.target.value })} options={STAGES} />
            <Input label="Deal Value (₹)" type="number" value={form.deal_value} onChange={e => setForm({ ...form, deal_value: e.target.value })} placeholder="0" />
          </div>
          <Input label="Address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Address" />
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6, fontWeight: 500 }}>Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Add notes..." style={{ width: '100%', height: 80, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', color: 'var(--text)', resize: 'none', fontSize: 13 }} />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Btn type="submit" disabled={saving} full>{saving ? 'Saving...' : 'Save Contact'}</Btn>
            <Btn variant="outline" onClick={() => setModal(false)} full>Cancel</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
